import { useCallback, useEffect, useRef } from "react";
import { ChatStore } from "@/stores/ChatStore";

interface UseIdleTimeoutProps {
  callback: () => Promise<void> | void;
  idleTimeout?: number; // in milliseconds; defaults to 5 minutes
  enabled?: boolean;
}

export default function useIdleTimeout({
  callback,
  idleTimeout = 5 * 60 * 1000,
  enabled = true,
}: UseIdleTimeoutProps): { resetIdleTimer: () => void } {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const idleTimeoutForceToken = ChatStore(state => state.idleTimeoutForceToken);
  // Seed with the current token so a remount (e.g. RealtimeHandler after restart)
  // does not replay an already-consumed force-timeout signal.
  const lastHandledForceToken = useRef(idleTimeoutForceToken);

  const clearIdleTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const resetIdleTimer = useCallback(() => {
    clearIdleTimer();
    if (!enabled) return;
    timer.current = setTimeout(() => {
      callbackRef.current();
    }, idleTimeout);
  }, [clearIdleTimer, enabled, idleTimeout]);

  useEffect(() => {
    if (!enabled) {
      clearIdleTimer();
      return;
    }
    resetIdleTimer();
    return clearIdleTimer;
  }, [enabled, resetIdleTimer, clearIdleTimer]);

  // Hidden test codeword increments idleTimeoutForceToken → fire the real idle callback.
  useEffect(() => {
    if (!enabled || idleTimeoutForceToken === 0) return;
    if (idleTimeoutForceToken === lastHandledForceToken.current) return;
    lastHandledForceToken.current = idleTimeoutForceToken;
    clearIdleTimer();
    void callbackRef.current();
  }, [idleTimeoutForceToken, enabled, clearIdleTimer]);

  return { resetIdleTimer };
}
